import DB from '../../Data/DB';
import API from '../../Services/API';
import { modelCallback, makeStateKey } from '../../Data/Model';
import State from '../../Data/State';
import { syncCompleteCallback } from './index';
class OfflineTaskQueue {

    TASK_MARK_JOB_DONE = 'mark_job_done';

    constructor() {
        this.queue = {};
        this.status = {
            pending: 0,
            synced: 0,
            failed: 0,
        };
        this.db = DB.table('offlineQueue');
        this.validTasks = [
            this.TASK_MARK_JOB_DONE
        ];
        this.syncing = false;
    }

    updateStatus() {
        this.status = {
            pending: Object.values(this.queue).filter(job => job.status === 'pending').length,
            synced: Object.values(this.queue).filter(job => job.status === 'synced').length,
            failed: Object.values(this.queue).filter(job => job.status === 'failed').length,
        };
    }

    async init() {
        const data = await this.db.getAllData();
        if (!data) {
            return;
        }
        for (const id in data) {
            this.queue[id] = data[id];
        }
        this.updateStatus();
    }

    async add(task, entity, data, status = 'pending') {
        if (!this.validTasks.includes(task)) {
            throw new Error(`Task ${task} not found`);
        }
        if (!entity.model || !entity.id) {
            throw new Error('Entity must have a model and id');
        }

        const id = this.makeId(task, entity);

        this.queue[id] = { id, task, entity, status, data };
        await this.db.set(id, this.queue[id]);
        this.updateStatus();
    }

    has(task, entity) {
        const id = this.makeId(task, entity);
        return this.queue[id]?.status ?? false;
    }

    get(task, entity) {
        const id = this.makeId(task, entity);
        return this.queue[id] ?? null;
    }

    listFailed() {
        return Object.values(this.queue).filter(job => job.status === 'failed');
    }

    async remove(task, entity) {
        const id = this.makeId(task, entity);
        delete this.queue[id];
        await this.db.remove(id);
        this.updateStatus();
    }

    makeId(task, entity) {
        return `${task}:${entity.model}:${entity.id}`;
    }

    async sync() {
        if (this.syncing) {
            return Promise.reject(new Error('Sync already in progress'));
        }
        this.syncing = true;

        try {
            for (const id in this.queue) {
                const task = this.queue[id];
                if (task.task === this.TASK_MARK_JOB_DONE && task.status !== 'synced') {
                    await this.markJobAsDone(task.entity, task.data);
                }
                this.updateStatus();
            }

            await this.removeSyncedJobs();
            this.updateStatus();
            syncCompleteCallback.invoke();
            return Promise.resolve();
        } catch (error) {
            return Promise.reject(error);
        } finally {
            this.syncing = false;
        }
    }

    async markJobAsDone(entity, data) {
        try {
            const stateKey = makeStateKey(['customer', entity.customerId]);

            await API.post(
                `/customers/${entity.customerId}/jobs/${entity.id}/done`,
                data,
                {handleConflict: false},
            );

            await this.add(this.TASK_MARK_JOB_DONE, entity, data, 'synced');

            const customer = await API.get(`/customers/${entity.customerId}`);

            State.put(stateKey, customer);
            modelCallback.invoke(stateKey, customer);
        } catch (error) {
            console.error(error);
            await this.add(this.TASK_MARK_JOB_DONE, entity, data, 'failed');
        }
    }

    async removeSyncedJobs() {
        for (const id in this.queue) {
            const task = this.queue[id];
            if (task.status === 'synced') {
                await this.remove(task.task, task.entity);
            }
        }
    }
}

export default new OfflineTaskQueue();
